import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, Box, Button, Popover, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useClients, useSelectedClient } from "../../../../Queries/ClientQueries";
import { useCurrentUser } from "../../../../Queries/UserQueries";
import { IClient } from "../../../../Typings/Client";
import { IUser } from "../../../../Typings/User";
import { ClientSelector } from "./Subcomponents/ClientSelector";
import { UserMenuButtons } from "./Subcomponents/UserMenuButtons";

export const generateUserDisplayName = (user: IUser) => {
  return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.nickname || "";
};

export const UserMenu = () => {
  const user = useCurrentUser();
  const allClients = useClients();
  const { data: selectedClient } = useSelectedClient();
  const { logout } = useAuth0();
  const [searchVal, setSearchVal] = useState("");
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const [anchorElClients, setAnchorElClients] = useState<null | HTMLElement>(null);
  const handleOpenClientsMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElClients(event.currentTarget);
  };
  const handleCloseClientsMenu = () => {
    setAnchorElClients(null);
  };
  const filteredClients = allClients.clients.filter(
    (client: IClient) =>
      client.clientName.toUpperCase().includes(searchVal.toUpperCase()) || searchVal === "",
  );
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const displayName = generateUserDisplayName(user);

  return (
    <Box sx={{ flex: "none", width: "auto", ml: { xs: 0, lg: "auto" } }}>
      <Tooltip title="Open settings">
        <Button
          data-test="profile-menu-button"
          aria-controls="menu-userprofile"
          aria-haspopup="true"
          onClick={handleOpenUserMenu}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          {user.picture ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                overflow: "hidden",
                mr: { xs: 0, md: 1 },
                "& img": { width: 1, height: 1 },
              }}
            >
              <img src={user.picture} alt={displayName} width={40} height={40} />
            </Box>
          ) : (
            <Avatar alt={displayName} sx={{ backgroundColor: "secondary.main", mr: { xs: 0, md: 2 } }}>
              {displayName.charAt(0)}
            </Avatar>
          )}

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "0.25em",
            }}
          >
            <Typography
              data-test="profile-menu-username"
              variant="body1"
              sx={{
                width: 1,
                textAlign: "left",
                fontWeight: "bold",
                lineHeight: 1,
              }}
            >
              {displayName}
            </Typography>
            <Typography
              data-test="profile-menu-selectedclient"
              variant="body2"
              sx={{ fontWeight: "normal", lineHeight: 1, margin: 0 }}
            >
              {selectedClient?.clientName}
            </Typography>
          </Box>
        </Button>
      </Tooltip>
      <Popover
        sx={{ mt: "45px" }}
        id="menu-userprofile"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <UserMenuButtons
          user={user}
          clients={allClients.clients}
          handleCloseUserMenu={handleCloseUserMenu}
          handleOpenClientsMenu={handleOpenClientsMenu}
          logout={logout}
        />
        <Popover
          id="menu-clientselector"
          anchorEl={anchorElClients}
          anchorOrigin={{
            vertical: isMobileScreen ? "top" : "center",
            horizontal: isMobileScreen ? "right" : "left",
          }}
          keepMounted
          transformOrigin={{
            vertical: isMobileScreen ? "top" : "center",
            horizontal: "right",
          }}
          open={Boolean(anchorElClients)}
          onClose={handleCloseClientsMenu}
          sx={{
            mt: "45px",
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <ClientSelector
            searchVal={searchVal}
            setSearchVal={setSearchVal}
            filteredClients={filteredClients}
            handleCloseUserMenu={handleCloseUserMenu}
            handleCloseClientsMenu={handleCloseClientsMenu}
          />
        </Popover>
      </Popover>
    </Box>
  );
};
